'use client';

import { useCallback, useRef } from 'react';
import { analyticsService } from '../../services';
import { ANALYTICS_EVENT_NAMES } from '../../utils/analytics/constants';
import type { AnalyticsProps } from '../../types/common';

interface UseCarouselSwipeTrackingProps {
  /**
   * Analytics configuration containing module metadata
   */
  analyticsConfig: Pick<
    AnalyticsProps,
    'moduleName' | 'componentFamily' | 'componentType' | 'sectionHeader'
  >;
  /**
   * Total number of items in the carousel
   */
  totalItems: number;
}

export interface CarouselSwipeHandler {
  /**
   * Callback to track swipe events
   * @param newIndex - The index the carousel moved to (0-based)
   * @param direction - Swipe direction ('left' = forward, 'right' = backward)
   * @param isTouch - Whether this was a touch swipe (true) or mouse drag (false)
   * @param totalDots - Total number of dots
   */
  (newIndex: number, direction: 'left' | 'right', isTouch: boolean, totalDots: number): void;
}

/**
 * Custom hook for tracking carousel swipe/drag interactions
 *
 * Provides a memoized callback that tracks carousel swipe events using the
 * new AnalyticsService singleton pattern. Automatically manages previous index
 * tracking for analytics.
 *
 * @example
 * ```tsx
 * const handleSwipe = useCarouselSwipeTracking({
 *   analyticsConfig: {
 *     moduleName: 'hero-carousel',
 *     componentFamily: 'carousel',
 *     componentType: 'hero',
 *     sectionHeader: heading
 *   },
 *   totalItems: items.length
 * });
 *
 * const carousel = useCarouselMultiCard({
 *   // ...other props
 *   onSwipe: (newIndex, direction, isTouch) => {
 *     handleSwipe(newIndex, direction, isTouch);
 *     setCurrentIndex(newIndex); // Handle state separately
 *   }
 * });
 * ```
 */
export function useCarouselSwipeTracking({
  analyticsConfig,
  totalItems,
}: UseCarouselSwipeTrackingProps): CarouselSwipeHandler {
  const previousIndexRef = useRef<number>(0);

  return useCallback(
    (newIndex: number, direction: 'left' | 'right', isTouch: boolean, totalDots: number) => {
      analyticsService.trackEvent({
        event: ANALYTICS_EVENT_NAMES.CAROUSEL_SWIPE,
        module_name: analyticsConfig.moduleName,
        component_family: analyticsConfig.componentFamily,
        component_type: analyticsConfig.componentType,
        ...(analyticsConfig.sectionHeader && {
          section_header: analyticsConfig.sectionHeader.toLowerCase(),
        }),
        swipe_direction: direction,
        interaction_type: isTouch ? 'swipe' : 'drag',
        current_index: newIndex + 1, // 1-based for analytics
        previous_index: previousIndexRef.current + 1, // 1-based for analytics
        total_items: totalItems,
        total_dots: totalDots,
      });

      // Update previous index for next swipe
      previousIndexRef.current = newIndex;
    },
    [analyticsConfig, totalItems]
  );
}
